import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import clientLogo from '../assets/brand/client_logo.png';
import logoWhiteWithText from '../assets/brand/logo-white-with-text.png';
import { useAuth } from '../auth/AuthContext';
import { clearSignInUserInfoCache, ensureSignInUserInfo, } from '../services/signinUserInfo';
import { clearIncidentLookupClientCache, ensureIncidentLookup, } from '../services/incidentLookup';
const ListResultCountContext = createContext(null);
export function useListResultCount() {
    const context = useContext(ListResultCountContext);
    if (!context) {
        throw new Error('useListResultCount must be used within Layout');
    }
    return context;
}
export function Layout({ children }) {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [detailError, setDetailError] = useState('');
    const [detailLoading, setDetailLoading] = useState(false);
    const [listCount, setListCount] = useState(null);
    const [listCountLoading, setListCountLoading] = useState(false);
    const menuRef = useRef(null);
    const setListResultCount = useCallback((count, loading = false) => {
        setListCount(count);
        setListCountLoading(loading);
    }, []);
    useEffect(() => {
        ensureSignInUserInfo()
            .then((info) => setDetail(info.detail))
            .catch((err) => console.error('[signinUserInfo] failed', err));
        // Warm Kokomo Incidents/lookup into the server-side cache for this session.
        ensureIncidentLookup().catch((err) => console.error('[incidentLookup] failed', err));
    }, []);
    useEffect(() => {
        if (!menuOpen)
            return;
        const onPointerDown = (event) => {
            if (!menuRef.current?.contains(event.target)) {
                setMenuOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape')
                setMenuOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [menuOpen]);
    const handleOpenMenu = async () => {
        const nextOpen = !menuOpen;
        setMenuOpen(nextOpen);
        if (!nextOpen)
            return;
        // Refresh when we don't have a role yet (e.g. first load before enrichment).
        if (detail?.roleName?.trim() || detail?.role?.name?.trim())
            return;
        setDetailLoading(true);
        setDetailError('');
        try {
            clearSignInUserInfoCache();
            const info = await ensureSignInUserInfo();
            setDetail(info.detail);
        }
        catch (err) {
            setDetailError(err instanceof Error ? err.message : 'Failed to load user details.');
        }
        finally {
            setDetailLoading(false);
        }
    };
    const handleLogout = () => {
        clearSignInUserInfoCache();
        clearIncidentLookupClientCache();
        setDetail(null);
        setMenuOpen(false);
        logout();
    };
    const displayName = detail
        ? [detail.firstName, detail.lastName].filter(Boolean).join(' ').trim() || detail.email
        : user?.name ?? user?.email ?? 'Account';
    const roleLabel = detail?.roleName?.trim() ||
        (typeof detail?.role?.name === 'string' ? detail.role.name.trim() : '') ||
        '';
    const productName = process.env.HEADER_PRODUCT || 'Washington BTA Portal';
    const l1Active = location.pathname === '/';
    const listCountLabel = listCountLoading && listCount == null
        ? ' (…)'
        : listCount != null
            ? ` (${listCount} result${listCount !== 1 ? 's' : ''})`
            : '';
    return (_jsx(ListResultCountContext.Provider, { value: { setListResultCount }, children: _jsxs("div", { className: "app-layout", children: [_jsx("header", { className: "app-header", children: _jsxs("div", { className: "app-header-inner", children: [_jsxs(Link, { to: "/", className: "header-brand", "aria-label": `${productName} home`, children: [_jsx("span", { className: "header-brand-divider", "aria-hidden": "true" }), _jsx("span", { className: "header-product", children: productName })] }), _jsx("img", { src: clientLogo, alt: "", className: "header-client-logo", "aria-hidden": "true" }), _jsxs("div", { className: "header-actions", children: [_jsxs("div", { className: "user-menu", ref: menuRef, children: [_jsx("button", { type: "button", className: `user-label${menuOpen ? ' is-open' : ''}`, "aria-expanded": menuOpen, "aria-haspopup": "dialog", onClick: handleOpenMenu, children: _jsx("span", { className: "user-label-name", children: displayName }) }), menuOpen && (_jsxs("div", { className: "user-menu-panel", role: "dialog", "aria-label": "Current user details", children: [_jsx("h3", { className: "user-menu-title", children: "Signed in as" }), detailLoading && _jsx("p", { className: "user-menu-muted", children: "Loading\u2026" }), detailError && _jsx("p", { className: "user-menu-error", children: detailError }), detail && !detailLoading && (_jsxs("dl", { className: "user-menu-details", children: [_jsxs("div", { children: [_jsx("dt", { children: "ID" }), _jsx("dd", { className: "mono", children: detail.id })] }), _jsxs("div", { children: [_jsx("dt", { children: "Name" }), _jsx("dd", { children: [detail.firstName, detail.lastName].filter(Boolean).join(' ').trim() ||
                                                                            '—' })] }), _jsxs("div", { children: [_jsx("dt", { children: "Email" }), _jsx("dd", { children: detail.email || '—' })] }), _jsxs("div", { children: [_jsx("dt", { children: "Role" }), _jsx("dd", { children: roleLabel || '—' })] })] }))] }))] }), _jsx("button", { type: "button", className: "btn btn-header", onClick: handleLogout, children: "Sign out" })] })] }) }), _jsx("nav", { className: "app-nav", children: _jsxs("div", { className: "app-nav-inner", children: [_jsxs("div", { className: "nav-tabs", children: [_jsxs(Link, { to: "/", className: `nav-link${l1Active ? ' active' : ''}`, children: ["Risk/Threat Assessments", l1Active && listCountLabel ? (_jsx("span", { className: "nav-link-count", children: listCountLabel })) : null] }), location.pathname.startsWith('/referrals/') && (_jsx("span", { className: "nav-link active", "aria-current": "page", children: "L1 Details" }))] }), _jsxs(Link, { to: "/new", className: "nav-link nav-link-primary", onClick: (event) => {
                                    // Always start a blank L1 — including when already on /new.
                                    event.preventDefault();
                                    navigate('/new', { state: { fresh: Date.now() } });
                                }, children: [_jsx("span", { className: "nav-new-icon", "aria-hidden": "true", children: "+" }), "New L1"] })] }) }), _jsx("main", { className: "app-main", children: children }), _jsx("footer", { className: "app-footer", children: _jsxs("div", { className: "app-footer-inner", children: [_jsxs("div", { className: "app-footer-powered", children: [_jsx("span", { className: "app-footer-powered-label", children: "Powered By" }), _jsx("img", { src: logoWhiteWithText, alt: "Kokomo 24/7", className: "app-footer-powered-logo" })] }), _jsxs("p", { children: ["\u00A9 2026", ' ', _jsx("a", { href: "https://www.kokomo247.com", target: "_blank", rel: "noopener noreferrer", children: "Kokomo Solutions, Inc." }), ' ', "All rights reserved."] }), _jsx("p", { children: "Kokomo24/7\u00AE, Personal Safety Cloud\u00AE, and TruScore\u00AE are registered trademarks." }), _jsx("p", { className: "app-footer-privacy", children: "Kokomo Solutions is dedicated to serve our clients and will not expose or sell your personally identifiable information to external entities such as telemarketers or marketing agencies. The sole purpose of the collected information is to create, develop, operate, deliver and improve our products and services. Because the growth of our business rests upon trustworthy relationships with our clients, we do not condone actions that could jeopardize this relationship." })] }) })] }) }));
}
